import apiService from './api';
import type { ApiResponse } from '../types';
import type {
  XdockSchedule,
  XdockFilePreview,
  SaveXdockSchedulePayload,
  XdockPickAnswer,
  XdockSettings,
  StorePickDays,
  XdockStaleOrder,
  XdockMoveResult,
  PoImportFile,
  PoImportPreview,
  PoImportConfirmed,
  RememberMatchPayload,
  RetailerRange,
  RetailerRangeQuery,
} from '../types/nurseryRetail';

/**
 * Retailer purchase orders and cross dock schedules. Backend: HHNodeServer
 * routes/nurseryRetail.js. Plan: docs/retailer-po-and-cross-dock-plan.md.
 *
 * Importing and the schedules need a nursery manager or admin seat; the days
 * a store is picked can be read by whoever reads customers.
 */

// A batch of purchase orders is judged line by line; the default 10 s is not
// enough for thirty of them.
const SLOW = { timeout: 120_000 };

const rangeQuery = (q: RetailerRangeQuery) => {
  const params = new URLSearchParams({ customer_id: String(q.customer_id), farm_id: String(q.farm_id), min_free: String(q.min_free) });
  if (q.supplier_range) params.set('supplier_range', q.supplier_range);
  return params.toString();
};

class NurseryRetailService {
  // ── Cross dock schedules ──

  async getSchedules(): Promise<ApiResponse<XdockSchedule[]>> {
    return apiService.get('/nursery/xdock-schedules');
  }

  async getSchedule(id: number): Promise<ApiResponse<XdockSchedule>> {
    return apiService.get(`/nursery/xdock-schedules/${id}`);
  }

  /** Read a pick list file. Writes nothing. */
  async previewSchedule(fileName: string, dataBase64: string): Promise<ApiResponse<XdockFilePreview>> {
    return apiService.post('/nursery/xdock-schedules/preview', { file_name: fileName, data_base64: dataBase64 }, undefined, SLOW);
  }

  async saveSchedule(data: SaveXdockSchedulePayload): Promise<ApiResponse<XdockSchedule>> {
    return apiService.post('/nursery/xdock-schedules', data);
  }

  async makeDefault(id: number): Promise<ApiResponse<XdockSchedule>> {
    return apiService.post(`/nursery/xdock-schedules/${id}/default`, {});
  }

  async removeSchedule(id: number): Promise<ApiResponse<{ id: number }>> {
    return apiService.delete(`/nursery/xdock-schedules/${id}`);
  }

  async getSettings(): Promise<ApiResponse<XdockSettings>> {
    return apiService.get('/nursery/xdock-settings');
  }

  async saveSettings(sameDayCutoffMinutes: number): Promise<ApiResponse<XdockSettings>> {
    return apiService.put('/nursery/xdock-settings', { same_day_cutoff_minutes: sameDayCutoffMinutes });
  }

  /** The answer a purchase order for this store and date would get. */
  async lookup(storeCode: string, date: string): Promise<ApiResponse<XdockPickAnswer>> {
    const q = new URLSearchParams({ store_code: storeCode, date });
    return apiService.get(`/nursery/xdock-schedules/lookup?${q}`);
  }

  async getStorePickDays(customerId: number, date: string): Promise<ApiResponse<StorePickDays>> {
    return apiService.get(`/nursery/xdock-schedules/store-days?customer_id=${customerId}&date=${date}`);
  }

  /** Open orders a schedule change has left on the wrong day. */
  async recheck(): Promise<ApiResponse<XdockStaleOrder[]>> {
    return apiService.get('/nursery/xdock-schedules/recheck');
  }

  async moveOrders(orderIds: number[]): Promise<ApiResponse<XdockMoveResult[]>> {
    return apiService.post('/nursery/xdock-schedules/recheck/move', { order_ids: orderIds }, undefined, SLOW);
  }

  // ── Purchase order import ──

  /** Read and judge the files. Writes nothing. */
  async previewPurchaseOrders(files: PoImportFile[]): Promise<ApiResponse<PoImportPreview>> {
    return apiService.post('/nursery/po-imports/preview', { files }, undefined, SLOW);
  }

  /** Make an order for every purchase order that is ready. The server judges each again. */
  async confirmPurchaseOrders(files: PoImportFile[]): Promise<ApiResponse<PoImportConfirmed>> {
    return apiService.post('/nursery/po-imports/confirm', { files }, undefined, SLOW);
  }

  /** Say which of ours a retailer's barcode and item number mean. Remembered for next time. */
  async rememberMatch(data: RememberMatchPayload): Promise<ApiResponse<{ remembered: { code_type: string; code: string }[] }>> {
    return apiService.put('/nursery/retailer-item-codes', data);
  }

  // ── Range upload ──

  /** Every tree on the head office's price list, and whether it goes in the file. */
  async getRange(q: RetailerRangeQuery): Promise<ApiResponse<RetailerRange>> {
    return apiService.get(`/nursery/retailer-range?${rangeQuery(q)}`, SLOW);
  }

  /** The file Bunnings' app takes. */
  async getRangeFile(q: RetailerRangeQuery): Promise<Blob> {
    return apiService.get(`/nursery/retailer-range/file?${rangeQuery(q)}`, { ...SLOW, responseType: 'blob' }) as unknown as Promise<Blob>;
  }
}

const nurseryRetailService = new NurseryRetailService();
export default nurseryRetailService;
